/*
Inspection-scheduling page (STREAM-38, v3 recipient-lifecycle design).

A property contact lands here from a tokenized SMS link (no login). The same
link is the order's scheduling status page: in the action_needed phase it
renders the capture wizard (landing -> grouped question steps -> availability
windows -> submit); in every later phase it renders that phase's status
screen (matching / confirmed / day-of / complete). Wrong-contact and
escalation branches, plus the expired-link "text me a new link" edge, hang
off the same shell.

The question set is served by the backend (GET -> questionnaire.questions) so
steps render from the definition; known keys slot into themed steps and any
unknown detail-backed questions fall into the final step, so definition
changes don't drop questions. The availability question routes to the
window-card picker (bottom-sheet editor) instead of a form field.

Submit is one PUT with { availability, answers } - both writes are
replace-style server-side, so re-submitting from the same link just updates.
*/

import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Form, Input, Radio } from 'antd'
import moment from 'moment-timezone'

import * as actions from 'store/actions/inspectionScheduling'
import AvailabilityWindows, { newWindow } from 'components/inspectionScheduling/AvailabilityWindows'
import { InfoCard, InfoRow, Screen, StatusPill } from 'components/inspectionScheduling/chrome'
import {
  CompleteScreen,
  ConfirmedScreen,
  DayOfScreen,
  MatchingScreen,
} from 'components/inspectionScheduling/StatusScreens'
import {
  EscalationDone,
  EscalationForm,
  ExpiredScreen,
  WrongContactConfirm,
  WrongContactDone,
} from 'components/inspectionScheduling/Branches'
import Loading from 'components/Loading'

const { TextArea } = Input

const OTHER = 'other'
const AVAILABILITY_TARGET = 'calendar_availability'

// Themed steps in mockup order; unknown detail questions fall into the last group.
export const STEP_GROUPS = [
  { eyebrow: 'Access & arrival', keys: ['access_method', 'who_present'] },
  { eyebrow: 'Occupancy & safety', keys: ['occupancy', 'safety_notes'] },
  { eyebrow: 'Day-of contact', keys: ['best_contact'], availability: true },
  { eyebrow: 'One last thing', keys: ['additional_notes'], catchAll: true },
]

const contactLabel = contact =>
  contact.role ? contact.full_name + ' (' + contact.role + ')' : contact.full_name

/* Pre-fill helpers -------------------------------------------------------- */

export const initialFormValues = (questions, answers, contacts) => {
  const values = {}
  questions.forEach(question => {
    const answer = answers[question.key]
    if (question.response_target === AVAILABILITY_TARGET) {
      return
    }
    if (question.type === 'select_with_other' && answer) {
      const known = (question.options || []).some(option => option.value === answer)
      values[question.key] = known ? answer : OTHER
      if (!known) {
        values[question.key + '_other'] = answer
      }
    } else if (answer) {
      values[question.key] = answer
    }
  })
  if (!values.best_contact && contacts.length) {
    const primary = contacts.find(contact => contact.is_primary_contact)
    values.best_contact = (primary || contacts[0]).id
  }
  return values
}

export const initialWindows = (availability, timezone) => {
  return (availability || [])
    .filter(event => event.start && event.end)
    .map(event => {
      const start = timezone ? moment(event.start).tz(timezone) : moment(event.start)
      const end = timezone ? moment(event.end).tz(timezone) : moment(event.end)
      return { ...newWindow(), date: start.clone().startOf('day'), times: [start, end] }
    })
}

/* Submit composition ------------------------------------------------------ */

export const composeAvailability = (windows, timezone) =>
  windows
    .filter(w => w.date && w.times && w.times[0] && w.times[1])
    .map(w => {
      const day = w.date.format('YYYY-MM-DD')
      const compose = time => {
        const stamp = day + ' ' + time.format('HH:mm')
        const value = timezone
          ? moment.tz(stamp, 'YYYY-MM-DD HH:mm', timezone)
          : moment(stamp, 'YYYY-MM-DD HH:mm')
        return value.toISOString(true)
      }
      return { start: compose(w.times[0]), end: compose(w.times[1]) }
    })

export const composeAnswers = (questions, values) => {
  const answers = {}
  questions.forEach(question => {
    if (question.response_target === AVAILABILITY_TARGET) {
      return
    }
    let value = values[question.key]
    if (question.type === 'select_with_other' && value === OTHER) {
      value = values[question.key + '_other']
    }
    if (value !== undefined && value !== null && value !== '') {
      answers[question.key] = value
    }
  })
  return answers
}

/* Step layout -------------------------------------------------------------- */

// Slot detail-backed questions into the themed groups; unknown keys join the
// catch-all group so new definition questions still render.
export const buildSteps = questions => {
  const detailQuestions = questions.filter(q => q.response_target !== AVAILABILITY_TARGET)
  const byKey = {}
  detailQuestions.forEach(q => { byKey[q.key] = q })
  const claimed = STEP_GROUPS.reduce((acc, g) => acc.concat(g.keys), [])
  const extras = detailQuestions.filter(q => claimed.indexOf(q.key) === -1)

  return STEP_GROUPS
    .map(group => {
      const groupQuestions = group.keys.map(key => byKey[key]).filter(Boolean)
      return {
        ...group,
        questions: group.catchAll ? groupQuestions.concat(extras) : groupQuestions,
      }
    })
    .filter(group => group.questions.length || group.availability)
}

// "N of 7" progress: 1-based ordinal of the step's first question across the
// whole definition (availability counts as a question, matching the mockup).
export const stepProgress = (steps, stepIndex, totalQuestions) => {
  let ordinal = 1
  for (let i = 0; i < stepIndex; i++) {
    ordinal += steps[i].questions.length + (steps[i].availability ? 1 : 0)
  }
  const total = totalQuestions || 1
  const clamped = Math.min(ordinal, total)
  return { ordinal: clamped, total, percent: Math.round((clamped / total) * 100) }
}

/* Question renderer ------------------------------------------------------- */

const QuestionField = ({ question, contacts, selectedValue }) => {
  const { key, prompt, type, required, options, help_text: helpText } = question
  const rules = required ? [{ required: true, message: 'This one is required.' }] : []

  if (type === 'text') {
    return (
      <div className='isp-question'>
        <div className='isp-question-label'>
          {prompt}
          {!required && <span className='isp-optional'>Optional</span>}
        </div>
        {helpText && <div className='isp-question-help'>{helpText}</div>}
        <Form.Item name={key} rules={rules} className='isp-item'>
          <TextArea rows={3} maxLength={2000} className='isp-textarea' placeholder='Optional but helpful' />
        </Form.Item>
      </div>
    )
  }

  const radioOptions = key === 'best_contact'
    ? contacts.map(contact => ({ value: contact.id, label: contactLabel(contact) }))
    : (options || [])

  return (
    <div className='isp-question'>
      <div className='isp-question-label'>{prompt}</div>
      {helpText && <div className='isp-question-help'>{helpText}</div>}
      <Form.Item name={key} rules={rules} className='isp-item'>
        <Radio.Group className='isp-radio-stack'>
          {radioOptions.map(option => (
            <Radio key={option.value} value={option.value} className='isp-radio'>
              {option.label}
            </Radio>
          ))}
        </Radio.Group>
      </Form.Item>
      {type === 'select_with_other' && selectedValue === OTHER && (
        <Form.Item
          name={key + '_other'}
          rules={[{ required: true, message: 'Please add a short description.' }]}
          className='isp-item'
        >
          <Input maxLength={500} className='isp-input' placeholder='Please describe' />
        </Form.Item>
      )}
    </div>
  )
}

/* Chrome pieces ------------------------------------------------------------ */

const ProgressBar = ({ ordinal, total, percent }) => (
  <div className='isp-progress'>
    <div className='isp-progress-track'>
      <div className='isp-progress-fill' style={{ width: percent + '%' }} />
    </div>
    <span className='isp-progress-label'>{ordinal} of {total}</span>
  </div>
)

/* Page -------------------------------------------------------------------- */

const LANDING = -1

// Signals the GET failure was an expired-but-verifiable token (request-link
// still works) rather than a bad/unknown one.
const isExpiredFailure = result =>
  result.status === 410 ||
  (result.data && (result.data.expired === true || result.data.code === 'link_expired'))

const InspectionScheduling = ({ match }) => {
  const dispatch = useDispatch()
  const { token } = match.params
  const [form] = Form.useForm()

  const [page, setPage] = useState(null)
  const [initValues, setInitValues] = useState({})
  const [loadError, setLoadError] = useState(null)
  const [loadExpired, setLoadExpired] = useState(false)
  const [stepIndex, setStepIndex] = useState(LANDING)
  const [windows, setWindows] = useState([])
  const [availabilityError, setAvailabilityError] = useState(null)
  const [submitError, setSubmitError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  // null | 'wrong_confirm' | 'wrong_done' | 'escalate' | 'escalate_done'
  const [branch, setBranch] = useState(null)
  const [branchSubmitting, setBranchSubmitting] = useState(false)
  const [branchError, setBranchError] = useState(null)
  const [escalationDetail, setEscalationDetail] = useState(null)
  // select_with_other values drive the conditional describe input.
  const [, setRenderTick] = useState(0)

  // The Form isn't mounted until page state is set, so pre-fill goes through
  // initialValues (setFieldsValue on an unmounted form is dropped).
  const applyPage = useCallback((data) => {
    const questions = (data.questionnaire && data.questionnaire.questions) || []
    setInitValues(initialFormValues(questions, data.answers || {}, data.contacts || []))
    setWindows(initialWindows(data.availability, data.order && data.order.property_timezone))
    setPage(data)
  }, [])

  useEffect(() => {
    dispatch(actions.loadSchedulingPage(token)).then(result => {
      if (result.error) {
        setLoadError(result.error)
        setLoadExpired(isExpiredFailure(result))
      } else {
        applyPage(result.data)
      }
    })
  }, [dispatch, token, applyPage])

  if (loadError) {
    if (loadExpired) {
      return (
        <ExpiredScreen
          lenderName={null}
          onRequestLink={() => dispatch(actions.requestNewLink(token))}
        />
      )
    }
    return (
      <Screen lenderName={null} centered testId='scheduling-invalid'>
        <StatusPill tone='action' label='Link problem' />
        <h1 className='isp-display isp-serif'>This link isn&rsquo;t working</h1>
        <p className='isp-lede'>{loadError}</p>
      </Screen>
    )
  }

  if (!page) {
    return <Loading />
  }

  const status = page.status || {}
  const lenderName = page.order && page.order.lender_name
  const questions = (page.questionnaire && page.questionnaire.questions) || []
  const steps = buildSteps(questions)
  const totalQuestions = questions.length
  const timezone = page.order && page.order.property_timezone
  const timezoneAbbr = timezone ? moment.tz(timezone).format('z') : ''
  const firstName = page.contact && page.contact.firstname

  /* ---- branches (wrong contact overrides everything, incl. phase) ---- */

  if (status.wrong_contact_reported || branch === 'wrong_done') {
    return <WrongContactDone lenderName={lenderName} />
  }

  if (branch === 'wrong_confirm') {
    const confirmWrongContact = async suggestion => {
      setBranchSubmitting(true)
      setBranchError(null)
      const result = await dispatch(actions.reportWrongContact({ token, suggestedContact: suggestion }))
      setBranchSubmitting(false)
      if (result.error) {
        setBranchError(result.error)
      } else {
        setBranch('wrong_done')
      }
    }
    return (
      <WrongContactConfirm
        lenderName={lenderName}
        submitting={branchSubmitting}
        error={branchError}
        onConfirm={confirmWrongContact}
        onBack={() => { setBranchError(null); setBranch(null) }}
      />
    )
  }

  if (branch === 'escalate_done') {
    return <EscalationDone page={page} lenderName={lenderName} detail={escalationDetail} />
  }

  if (branch === 'escalate') {
    const sendEscalation = async detail => {
      setBranchSubmitting(true)
      setBranchError(null)
      const result = await dispatch(actions.submitEscalation({
        token,
        availabilityText: detail.availabilityText,
        preferredChannel: detail.preferredChannel,
      }))
      setBranchSubmitting(false)
      if (result.error) {
        setBranchError(result.error)
      } else {
        setEscalationDetail(detail)
        setBranch('escalate_done')
      }
    }
    return (
      <EscalationForm
        lenderName={lenderName}
        submitting={branchSubmitting}
        error={branchError}
        onSubmit={sendEscalation}
        onBack={() => { setBranchError(null); setBranch(null) }}
      />
    )
  }

  /* ---- phase routing: wizard only in action_needed ---- */

  if (submitted || status.phase === 'matching') {
    return <MatchingScreen page={page} lenderName={lenderName} />
  }
  if (status.phase === 'confirmed') {
    return <ConfirmedScreen page={page} status={status} lenderName={lenderName} />
  }
  if (status.phase === 'day_of') {
    return <DayOfScreen page={page} status={status} lenderName={lenderName} />
  }
  if (status.phase === 'complete') {
    return <CompleteScreen page={page} status={status} lenderName={lenderName} />
  }
  if (status.escalation_requested) {
    return <EscalationDone page={page} lenderName={lenderName} detail={escalationDetail} />
  }

  /* ---- capture wizard (action_needed, or legacy payload with no status) ---- */

  const submitAll = async () => {
    const availability = composeAvailability(windows, timezone)
    const answers = composeAnswers(questions, form.getFieldsValue(true))
    setSubmitError(null)
    setSubmitting(true)
    const result = await dispatch(actions.submitSchedulingPage({ token, availability, answers }))
    setSubmitting(false)
    if (result.error) {
      setSubmitError(result.error)
    } else {
      // Fold the just-submitted data into the page so the matching screen's
      // summary card reflects it without a refetch.
      setPage({ ...page, availability, answers: { ...(page.answers || {}), ...answers } })
      setSubmitted(true)
    }
  }

  const advance = async () => {
    const step = steps[stepIndex]
    const fieldNames = step.questions.reduce((acc, q) => {
      acc.push(q.key)
      if (q.type === 'select_with_other' && form.getFieldValue(q.key) === OTHER) {
        acc.push(q.key + '_other')
      }
      return acc
    }, [])
    try {
      await form.validateFields(fieldNames)
    } catch (err) {
      return
    }
    if (step.availability && !composeAvailability(windows, timezone).length) {
      setAvailabilityError('Please add at least one date with a time range.')
      return
    }
    setAvailabilityError(null)
    if (stepIndex < steps.length - 1) {
      setStepIndex(stepIndex + 1)
    } else {
      submitAll()
    }
  }

  /* ---- landing ---- */
  if (stepIndex === LANDING) {
    return (
      <Screen lenderName={lenderName} testId='scheduling-landing'>
        <StatusPill tone='action' label='Action needed' />
        <h1 className='isp-display isp-serif'>Let&rsquo;s schedule your inspection</h1>
        <p className='isp-lede'>
          {firstName ? 'Hi ' + firstName + ' — ' : ''}
          <b>{lenderName || 'your lender'}</b> ordered a property inspection.
          Answer {totalQuestions} quick questions and we&rsquo;ll match you with
          an inspector.
        </p>
        {!page.status && page.order && page.order.is_scheduled && (
          <div className='isp-banner'>
            A visit is already scheduled for this property. You can still update your answers.
          </div>
        )}
        <InfoCard>
          <InfoRow label='Property'>{page.order && page.order.address}</InfoRow>
          {lenderName && <InfoRow label='For'>{lenderName}</InfoRow>}
          <InfoRow label='Takes about'>2 minutes</InfoRow>
        </InfoCard>
        <div className='isp-cta-stack'>
          <button className='isp-cta' data-testid='scheduling-start' onClick={() => setStepIndex(0)}>
            Get started
          </button>
          <button
            className='isp-link'
            data-testid='wrong-contact-link'
            onClick={() => setBranch('wrong_confirm')}
          >
            This isn&rsquo;t me &mdash; wrong contact
          </button>
        </div>
      </Screen>
    )
  }

  /* ---- wizard steps ---- */
  const step = steps[stepIndex]
  const progress = stepProgress(steps, stepIndex, totalQuestions)
  const isLast = stepIndex === steps.length - 1
  const goBack = () => setStepIndex(stepIndex === 0 ? LANDING : stepIndex - 1)

  return (
    <Screen lenderName={lenderName} onBack={goBack} testId='scheduling-step'>
      <StatusPill tone='action' label='Action needed' />
      <ProgressBar {...progress} />
      <Form
        form={form}
        initialValues={initValues}
        requiredMark={false}
        onValuesChange={() => setRenderTick(t => t + 1)}
      >
        <div className='isp-form-eyebrow'>{step.eyebrow}</div>
        {step.questions.map(question => (
          <QuestionField
            key={question.key}
            question={question}
            contacts={page.contacts || []}
            selectedValue={form.getFieldValue(question.key) || initValues[question.key]}
          />
        ))}
        {step.availability && (
          <div className='isp-question'>
            <div className='isp-form-eyebrow isp-eyebrow-gap'>When is the property available?</div>
            <div className='isp-question-help'>
              Pick the dates and time windows that work
              {timezoneAbbr ? ' - times are in the property’s local timezone (' + timezoneAbbr + ')' : ''}.
            </div>
            <AvailabilityWindows
              windows={windows}
              onChange={setWindows}
              error={availabilityError}
            />
            <button
              type='button'
              className='isp-link left'
              data-testid='escalation-link'
              onClick={() => setBranch('escalate')}
            >
              None of these times work
            </button>
          </div>
        )}
      </Form>
      {submitError && <div className='isp-error'>{submitError}</div>}
      <div className='isp-cta-stack'>
        <button className='isp-cta' data-testid='scheduling-continue' onClick={advance} disabled={submitting}>
          {submitting ? 'Submitting…' : isLast ? 'Submit availability' : 'Continue'}
        </button>
      </div>
    </Screen>
  )
}

export default InspectionScheduling
